/**
 * API error envelope + typed exceptions.
 *
 * The NestJS HttpExceptionFilter returns `{ error: { code, message, details? } }`
 * for every failure (see docs/api.md §1.1). Network/timeout failures (server
 * unreachable) are surfaced as `NetworkError`, which feature hooks use to decide
 * whether to fall back to mock data.
 */

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: unknown[];
}

/** A non-2xx HTTP response with the parsed error envelope. */
export class ApiException extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown[];

  constructor(status: number, error: ApiErrorBody) {
    super(error.message);
    this.name = "ApiException";
    this.status = status;
    this.code = error.code;
    this.details = error.details;
  }
}

/** Server unreachable / timed out (no HTTP response). Triggers mock fallback. */
export class NetworkError extends Error {
  constructor(message = "API injoignable") {
    super(message);
    this.name = "NetworkError";
  }
}

export function isApiException(err: unknown): err is ApiException {
  return err instanceof ApiException;
}

export function isNetworkError(err: unknown): err is NetworkError {
  return err instanceof NetworkError;
}

/** 401/403 — token missing/expired or insufficient permission. */
export function isUnauthorized(err: unknown): boolean {
  return isApiException(err) && (err.status === 401 || err.status === 403);
}

/** Human-friendly message for toasts and error states. */
export function getErrorMessage(err: unknown, fallback = "Une erreur est survenue"): string {
  if (isApiException(err) || isNetworkError(err)) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
